import { Component } from '@angular/core';
import { DriversService } from '../../service/drivers.service';
import { ProgressLoaderService } from '../../service/progress-loader.service';
import { MessageService } from 'primeng/api';
import { Router } from '@angular/router';
import { TableModule } from 'primeng/table';
import { DriverSummary } from '../../models/common-data-types';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { LanguageService } from '../../service/language.service';
import { DropdownChangeEvent } from 'primeng/dropdown';
import { TranslateService } from '@ngx-translate/core';
import { NgxTranslateModule } from 'src/app/translate.module';
import { TooltipModule } from 'primeng/tooltip';
import { ScreenSizeService } from '../../service/screen-size.service';
import { DriverListCardComponent } from '../common/driver-list-card/driver-list-card.component';
import { CustomToastrService } from '../../service/custom-toastr.service';
import { DividerModule } from 'primeng/divider';

@Component({
    selector: 'app-drivers',
    standalone: true,
    imports: [
        CommonModule,
        TableModule,
        ButtonModule,
        DividerModule,
        NgxTranslateModule,
        TooltipModule,
        DriverListCardComponent,
    ],
    templateUrl: './drivers.component.html',
    styleUrl: './drivers.component.scss',
})
export class DriversComponent {
    loading: boolean = false;
    selectedLanguage: any = '';
    drivers: DriverSummary[] = [];
    nonCompliantCount: number = 0;

    constructor(
        private driversService: DriversService,
        private progressLoaderService: ProgressLoaderService,
        private messageService: MessageService,
        private router: Router,
        private screenSizeService: ScreenSizeService,
        private languageService: LanguageService,
        private translate: TranslateService,
        private wiseToastr: CustomToastrService
    ) {}

    ngOnInit(): void {
        this.getCustomerDrivers();
        this.initializeLocale();
    }

    navigateToAddDriver() {
        this.router.navigate(['/home/drivers/new']);
    }

    viewDriverDetails(driver: any): void {
        this.router.navigate(['/home/drivers/'.concat(driver.id)]);
    }

    addDriverClicked(): void {
        this.router.navigate(['/home/drivers/new']);
    }

    getCustomerDrivers(): void {
        this.loading = true;
        this.progressLoaderService.show('Fetching your drivers');
        this.driversService.getDrivers().subscribe({
            next: (data) => {
                this.loading = false;
                this.drivers = data;
                this.nonCompliantCount = this.getNonCompliantCount(
                    this.drivers
                );
                this.progressLoaderService.hide();
            },
            error: (err) => {
                this.loading = false;
                this.progressLoaderService.hide();
                this.showErrorToast(this.translate.instant(err.error.messageKey));
            },
        });
    }

    getNonCompliantCount(drivers: DriverSummary[]): number {
        return drivers.filter((driver) => {
            return driver.kycStatus === 'NON_COMPLIANT';
        }).length;
    }

    rowClass(rowData: DriverSummary): string {
        return rowData.kycStatus === 'NON_COMPLIANT' ? 'call-to-action' : '';
    }

    updateDriver(id: string) {
        this.router.navigate(['/home/drivers/update-driver/' + id]);
    }

    deleteDriver(driverId: string) {
        this.progressLoaderService.show('Deleting driver');
        this.driversService.deleteDriver(driverId).subscribe({
            next: (res) => {
                this.progressLoaderService.hide();
                this.showSuccessToast('Driver deleted successfully');
                this.getCustomerDrivers();
            },
            error: (err) => {
                this.progressLoaderService.hide();
                this.showErrorToast(this.translate.instant(err.error.messageKey));
            },
        });
    }

    initializeLocale() {
        this.languageService.currentLanguage$.subscribe((language) => {
            this.selectedLanguage = language;
            this.setLanguage(this.selectedLanguage);
        });
    }

    showSuccessToast(message: string) {
        this.wiseToastr.showSuccess(message);
    }
    showErrorToast(message: string) {
        this.wiseToastr.showError(message);
    }

    setLanguage(language: string) {
        this.translate.use(language);
        this.languageService.setLanguage(language);
    }

    languageChanged(event: DropdownChangeEvent) {
        this.setLanguage(event.value);
    }

    viewDriver(driverId: string) {
        this.router.navigate([
            '/self-care/drivers/view-driver-detail',
            driverId,
        ]);
    }

    getDeviceType(): string {
        return this.screenSizeService.getDeviceType();
    }

    getDeviceHeight(): number {
        return this.screenSizeService.getScreenDimensions().height;
    }
}
